import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cx } from './cx';
import { useKit } from './strings';

export type KitButtonVariant = 'primary' | 'secondary' | 'ghost' | 'accent' | 'danger';
export type KitButtonSize = 'sm' | 'md' | 'lg';

type Common = { variant?: KitButtonVariant; size?: KitButtonSize; block?: boolean; iconStart?: ReactNode; iconEnd?: ReactNode };
export type KitButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & Common & { loading?: boolean; ref?: Ref<HTMLButtonElement> };

export function Spinner({ label }: { label?: string }) {
  return <span className="qk-spinner" role={label ? 'status' : undefined} aria-label={label} aria-hidden={label ? undefined : true} />;
}

/**
 * Pill button. primary = ink fill (white in dark), accent = lavender fill with black label (11.5:1),
 * secondary = hairline outline, ghost = text only, danger = destructive. One primary per view.
 * loading keeps the width, announces busy and blocks repeat submits.
 */
export function Button({ variant = 'secondary', size = 'md', block, loading, iconStart, iconEnd, className, children, disabled, type = 'button', ...rest }: KitButtonProps) {
  const { strings } = useKit();
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-variant={variant}
      data-size={size}
      data-block={block || undefined}
      className={cx('qk-btn', className)}
    >
      {loading ? <Spinner /> : iconStart ? <span className="qk-btn__icon">{iconStart}</span> : null}
      <span className="qk-btn__label">{children}</span>
      {loading ? <span className="qk-sr-only">{strings.loading}</span> : null}
      {iconEnd ? <span className="qk-btn__icon">{iconEnd}</span> : null}
    </button>
  );
}

/** Icon-only button. The accessible name is mandatory and doubles as the tooltip. */
export function IconButton({ label, children, variant = 'ghost', size = 'md', className, type = 'button', ...rest }: Omit<KitButtonProps, 'iconStart' | 'iconEnd' | 'loading' | 'block'> & { label: string }) {
  return (
    <button {...rest} type={type} aria-label={label} title={label} data-variant={variant} data-size={size} data-icon-only="" className={cx('qk-btn', className)}>
      <span className="qk-btn__icon">{children}</span>
    </button>
  );
}

/** A link styled as a button (navigation, not an action). */
export function ButtonLink({ variant = 'secondary', size = 'md', block, iconStart, iconEnd, className, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & Common) {
  return (
    <a {...rest} data-variant={variant} data-size={size} data-block={block || undefined} className={cx('qk-btn', className)}>
      {iconStart ? <span className="qk-btn__icon">{iconStart}</span> : null}
      <span className="qk-btn__label">{children}</span>
      {iconEnd ? <span className="qk-btn__icon">{iconEnd}</span> : null}
    </a>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="qk-kbd">{children}</kbd>;
}
